import React from "react";

/**
 * Render shared ticket log footer controls and count text.
 *
 * @param {Object} props - Component props.
 * @param {Object|null} props.paginationInfo - Pagination state from TicketLogTable.
 * @param {number} props.filteredCount - Count after TicketLogTable filters.
 * @param {number} props.totalCount - Total unfiltered item count.
 * @param {string} props.itemLabel - Singular item label.
 * @param {string} props.accentClassName - Text color for emphasized counts.
 * @param {string} props.selectFocusClassName - Focus classes for the page-size dropdown.
 * @param {React.ReactNode} props.endContent - Optional right-side footer content.
 * @returns {JSX.Element}
 */
const TicketLogFooter = ({
  paginationInfo,
  filteredCount,
  totalCount,
  itemLabel,
  accentClassName,
  selectFocusClassName = "focus:border-blue-500 focus:ring-blue-500",
  endContent = null,
}) => {
  const itemLabelText = `${itemLabel}${(paginationInfo?.shouldPaginate ? filteredCount : totalCount) !== 1 ? "s" : ""}`;

  return (
    <div className="flex flex-col gap-3 rounded-b-lg border-t border-gray-200 bg-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
        {paginationInfo?.shouldPaginate ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={paginationInfo.onPreviousPage}
              disabled={!paginationInfo.canGoPrevious}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <span className="min-w-[5rem] text-center font-medium text-gray-700">
              Page {paginationInfo.currentPage} of {paginationInfo.totalPages}
            </span>
            <button
              type="button"
              onClick={paginationInfo.onNextPage}
              disabled={!paginationInfo.canGoNext}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        ) : null}

        {paginationInfo?.shouldPaginate ? (
          <select
            value={paginationInfo.pageSize}
            onChange={(event) => paginationInfo.onPageSizeChange(event.target.value)}
            aria-label={`${itemLabel} per page`}
            className={`rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-1 ${selectFocusClassName}`}
          >
            {paginationInfo.pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option === "all" ? "All" : `${option} / page`}
              </option>
            ))}
          </select>
        ) : null}

        <p>
          {paginationInfo?.shouldPaginate ? (
            <>
              Showing{" "}
              <b className={accentClassName}>
                {paginationInfo.pageStart}-{paginationInfo.pageEnd}
              </b>{" "}
              of <b className={accentClassName}>{filteredCount}</b> {itemLabelText}
            </>
          ) : (
            <>
              Showing <b className={accentClassName}>{filteredCount}</b> of{" "}
              <b className={accentClassName}>{totalCount}</b> {itemLabelText}
            </>
          )}
        </p>
      </div>

      {endContent}
    </div>
  );
};

export default TicketLogFooter;
